import { useState, useEffect, useCallback } from 'react';
import {
  IntrospectDashboardState,
  InsightsData,
  SavedChart,
  ChatMessage,
  defaultDashboardState,
} from '../types/introspect';

const STORAGE_KEY = 'bigsplice-introspect-state';

/**
 * Hook to manage and persist Introspect dashboard state to localStorage
 * Persists: selected file, cached insights, saved charts, and layout preferences
 */
export const useIntrospectState = () => {
  const [state, setState] = useState<IntrospectDashboardState>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Migrate: merge with defaults to add any new properties
        return {
          ...defaultDashboardState,
          ...parsed,
          // Ensure nested objects exist
          insights: parsed.insights || {},
          chatHistory: parsed.chatHistory || {},
          savedCharts: parsed.savedCharts || [],
          layout: {
            ...defaultDashboardState.layout,
            ...(parsed.layout || {}),
          },
        };
      }
    } catch {
      // Ignore parse errors
    }
    return defaultDashboardState;
  });

  // Persist to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Ignore storage errors
    }
  }, [state]);

  // Select a file
  const selectFile = useCallback((fileKey: string | null) => {
    setState((prev) => ({
      ...prev,
      selectedFileKey: fileKey,
    }));
  }, []);

  // Cache insights for a file
  const cacheInsights = useCallback((fileKey: string, insights: InsightsData) => {
    setState((prev) => ({
      ...prev,
      insights: {
        ...prev.insights,
        [fileKey]: {
          data: insights,
          fetchedAt: new Date().toISOString(),
        },
      },
    }));
  }, []);

  // Get cached insights for a file
  const getCachedInsights = useCallback(
    (fileKey: string) => {
      return state.insights[fileKey]?.data ?? null;
    },
    [state.insights]
  );

  // Clear cached insights for a file (for refresh)
  const clearInsightsCache = useCallback((fileKey: string) => {
    setState((prev) => {
      const { [fileKey]: _, ...rest } = prev.insights;
      return {
        ...prev,
        insights: rest,
      };
    });
  }, []);

  // Save a chart
  const saveChart = useCallback((chart: SavedChart) => {
    setState((prev) => ({
      ...prev,
      savedCharts: [...prev.savedCharts, chart],
    }));
  }, []);

  // Delete a chart
  const deleteChart = useCallback((chartId: string) => {
    setState((prev) => ({
      ...prev,
      savedCharts: prev.savedCharts.filter((c) => c.id !== chartId),
    }));
  }, []);

  // Toggle chart pinned status
  const toggleChartPinned = useCallback((chartId: string) => {
    setState((prev) => ({
      ...prev,
      savedCharts: prev.savedCharts.map((c) =>
        c.id === chartId ? { ...c, pinned: !c.pinned } : c
      ),
    }));
  }, []);

  // Get saved charts for a file
  const getFileCharts = useCallback(
    (fileKey: string) => {
      return state.savedCharts.filter((c) => c.fileKey === fileKey);
    },
    [state.savedCharts]
  );

  // Toggle section collapsed
  const toggleSection = useCallback((sectionId: string) => {
    setState((prev) => {
      const collapsed = prev.layout.collapsedSections;
      const isCollapsed = collapsed.includes(sectionId);
      return {
        ...prev,
        layout: {
          ...prev.layout,
          collapsedSections: isCollapsed
            ? collapsed.filter((s) => s !== sectionId)
            : [...collapsed, sectionId],
        },
      };
    });
  }, []);

  // Check if section is collapsed
  const isSectionCollapsed = useCallback(
    (sectionId: string) => {
      return state.layout.collapsedSections.includes(sectionId);
    },
    [state.layout.collapsedSections]
  );

  // Set chart grid columns
  const setChartGridColumns = useCallback((columns: number) => {
    setState((prev) => ({
      ...prev,
      layout: {
        ...prev.layout,
        chartGridColumns: columns,
      },
    }));
  }, []);

  // Get chat history for a file
  const getChatHistory = useCallback(
    (fileKey: string) => {
      return state.chatHistory[fileKey] ?? null;
    },
    [state.chatHistory]
  );

  // Save chat history for a file
  const saveChatHistory = useCallback(
    (fileKey: string, messages: ChatMessage[], suggestedQuestions: string[]) => {
      setState((prev) => ({
        ...prev,
        chatHistory: {
          ...prev.chatHistory,
          [fileKey]: {
            messages,
            suggestedQuestions,
            lastUpdated: new Date().toISOString(),
          },
        },
      }));
    },
    []
  );

  // Clear chat history for a file
  const clearChatHistory = useCallback((fileKey: string) => {
    setState((prev) => {
      const { [fileKey]: _, ...rest } = prev.chatHistory;
      return {
        ...prev,
        chatHistory: rest,
      };
    });
  }, []);

  return {
    state,
    selectedFileKey: state.selectedFileKey,
    savedCharts: state.savedCharts,
    chartGridColumns: state.layout.chartGridColumns,
    selectFile,
    cacheInsights,
    getCachedInsights,
    clearInsightsCache,
    saveChart,
    deleteChart,
    toggleChartPinned,
    getFileCharts,
    toggleSection,
    isSectionCollapsed,
    setChartGridColumns,
    getChatHistory,
    saveChatHistory,
    clearChatHistory,
  };
};
